/**
 * Typed React Query hooks for API operations.
 *
 * These hooks provide fully typed data fetching with automatic caching,
 * refetching, and error handling via TanStack Query.
 * 
 * Authentication is handled via WorkOS AuthKit - access tokens are
 * automatically fetched and included in requests. When the session expires,
 * getAccessToken() throws and we redirect to sign in.
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query'
import { useAuth } from '@workos-inc/authkit-react'
import { useCallback, useRef } from 'react'
import { createAuthenticatedClient } from './client'
import type {
  EmailAccount,
  Instruction,
  InstructionCreate,
  InstructionUpdate,
  InstructionScope,
  DashboardStats,
  EventsList,
  EmailEventDetail,
} from './client'
import type { components, operations, paths } from './schema'

// =============================================================================
// Type-Safe Query Parameter Types
// =============================================================================
// These types are derived from the OpenAPI schema to ensure query parameters
// match exactly what the API expects. Using these prevents bugs like passing
// "email_account_id" when the API expects "connected_account_id".

type EventsQueryParams = NonNullable<operations['get_events_api_v1_events_get']['parameters']['query']>
type EventsStatsQueryParams = NonNullable<operations['get_events_stats_api_v1_events_stats_summary_get']['parameters']['query']>
type EventHeadersQueryParams = NonNullable<operations['get_event_headers_api_v1_events_headers_get']['parameters']['query']>
export type EventHeaders = components['schemas']['EventHeaders']
type InstructionsQueryParams = NonNullable<operations['list_instructions_api_v1_instructions_get']['parameters']['query']>

// =============================================================================
// Query Keys - centralized for consistency
// =============================================================================

export const queryKeys = {
  // Onboarding
  onboardingStatus: ['onboarding', 'status'] as const,

  // Inboxes
  inboxes: ['inboxes'] as const,
  inbox: (id: string) => ['inboxes', id] as const,
  classification: (id: string) => ['inboxes', id, 'classification'] as const,
  classificationTargets: (id: string) => ['inboxes', id, 'classification', 'targets'] as const,
  mailboxResources: (id: string) => ['inboxes', id, 'classification', 'resources'] as const,
  classificationStatus: (id: string) => ['inboxes', id, 'classification', 'status'] as const,

  // Instructions
  instructions: (params?: { scope?: InstructionScope; connectedAccountId?: string; mcpServerId?: string; toolName?: string }) =>
    ['instructions', params] as const,

  // Events
  events: (params?: { emailAccountId?: string; status?: string; limit?: number; offset?: number }) =>
    ['events', params] as const,
  event: (id: string) => ['events', id] as const,
  eventsStats: (params?: { emailAccountId?: string }) => ['events', 'stats', params] as const,

  // Dashboard
  dashboardStats: ['dashboard', 'stats'] as const,

  // Subscription
  subscription: ['subscription'] as const,
  subscriptionUsage: ['subscription', 'usage'] as const,
  subscriptionTiers: ['subscription', 'tiers'] as const,
} as const

// =============================================================================
// Helper hook to get authenticated API client
// Uses WorkOS AuthKit's getAccessToken() which handles refresh automatically.
// When session is expired, calls signIn() to redirect to authentication.
// =============================================================================

export function useAuthenticatedClient() {
  const { getAccessToken, signIn } = useAuth()
  // Track if we've already triggered a sign-in redirect to prevent loops
  const isRedirecting = useRef(false)
  
  return useCallback(async () => {
    try {
      // WorkOS SDK handles token refresh automatically
      const token = await getAccessToken()
      if (!token) {
        throw new Error('No access token available')
      }
      return createAuthenticatedClient(token)
    } catch (error) {
      // Session expired or refresh failed - redirect to sign in
      // Only redirect once to prevent loops from multiple concurrent requests
      if (!isRedirecting.current) {
        isRedirecting.current = true
        console.log('[Auth] Session expired, redirecting to sign in...')
        signIn()
      }
      throw error
    }
  }, [getAccessToken, signIn])
}

// =============================================================================
// Inbox Queries
// =============================================================================

export function useInboxes(
  options?: Omit<UseQueryOptions<EmailAccount[]>, 'queryKey' | 'queryFn'>
) {
  const getClient = useAuthenticatedClient()
  
  return useQuery({
    queryKey: queryKeys.inboxes,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/api/v1/inboxes')
      if (error) throw new Error('Failed to fetch inboxes')
      return data ?? []
    },
    ...options,
  })
}

// Customer-authored configuration and preview data are memory-only. Never put
// request bodies, provider errors, or source content into error/telemetry text.
export class ClassificationRequestError extends Error {
  readonly status: number
  readonly code: string | null
  constructor(status: number, payload?: unknown) {
    super('Classification request failed')
    this.status = status
    const detail = payload && typeof payload === 'object' && 'detail' in payload ? payload.detail : null
    const code = detail && typeof detail === 'object' && 'code' in detail ? detail.code : null
    this.code = typeof code === 'string' && ['config_changed', 'not_configured', 'missing_scopes', 'reconnect_required', 'target_missing', 'mailbox_already_controlled', 'legacy_work_running', 'gate_unavailable', 'activation_unavailable', 'resource_in_use', 'resource_forbidden', 'unsupported_operation', 'content_confirmation_required', 'invalid_resource', 'unknown_outcome', 'rate_limited', 'reserved_label_name'].includes(code) ? code : null
  }
}

export function useClassification(inboxId: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: queryKeys.classification(inboxId),
    queryFn: async () => {
      const client = await getClient()
      const { data, response } = await client.GET('/api/v1/inboxes/{inbox_id}/classification', { params: { path: { inbox_id: inboxId } }, cache: 'no-store' })
      if (!data || !response.ok) throw new ClassificationRequestError(response.status)
      return data
    },
    enabled: !!inboxId, gcTime: 0, retry: false, staleTime: 0, refetchOnWindowFocus: false,
  })
}

export function useSaveClassification(inboxId: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: components['schemas']['ClassificationSettingsPut']) => {
      const client = await getClient()
      const { data, response } = await client.PUT('/api/v1/inboxes/{inbox_id}/classification', { params: { path: { inbox_id: inboxId } }, body, cache: 'no-store' })
      if (!data || !response.ok) throw new ClassificationRequestError(response.status)
      return data
    },
    gcTime: 0, retry: false,
    onSuccess: data => { queryClient.setQueryData(queryKeys.classification(inboxId), data) },
  })
}

export function useClassificationTargets(inboxId: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({ queryKey: queryKeys.classificationTargets(inboxId), queryFn: async () => {
    const client = await getClient()
    const { data, error, response } = await client.GET('/api/v1/inboxes/{inbox_id}/classification/targets', { params: { path: { inbox_id: inboxId } }, cache: 'no-store' })
    if (!data || !response.ok) throw new ClassificationRequestError(response.status, error)
    return data
  }, enabled: !!inboxId, gcTime: 0, retry: false })
}

export function useMailboxResources(inboxId: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({ queryKey: queryKeys.mailboxResources(inboxId), queryFn: async () => {
    const client = await getClient()
    const { data, error, response } = await client.GET('/api/v1/inboxes/{inbox_id}/classification/resources', { params: { path: { inbox_id: inboxId } }, cache: 'no-store' })
    if (!data || !response.ok) throw new ClassificationRequestError(response.status, error)
    return data
  }, enabled: !!inboxId, gcTime: 0, retry: false })
}

type ResourceCreateOperation = paths['/api/v1/inboxes/{inbox_id}/classification/resources/{kind}']['post']
type ResourceUpdateOperation = paths['/api/v1/inboxes/{inbox_id}/classification/resources/{kind}/{resource_id}']['patch']
export type MailboxResourceKind = ResourceCreateOperation['parameters']['path']['kind']

export function useCreateMailboxResource(inboxId: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({ gcTime: 0, retry: false, mutationFn: async ({ kind, body }: { kind: MailboxResourceKind; body: ResourceCreateOperation['requestBody']['content']['application/json'] }) => {
    const client = await getClient()
    const { data, error, response } = await client.POST('/api/v1/inboxes/{inbox_id}/classification/resources/{kind}', { params: { path: { inbox_id: inboxId, kind } }, body, cache: 'no-store' })
    if (!data || !response.ok) throw new ClassificationRequestError(response.status, error)
    return data
  }, onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.classification(inboxId) }) })
}

export function useUpdateMailboxResource(inboxId: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({ gcTime: 0, retry: false, mutationFn: async ({ kind, id, body }: { kind: MailboxResourceKind; id: string; body: ResourceUpdateOperation['requestBody']['content']['application/json'] }) => {
    const client = await getClient()
    const { data, error, response } = await client.PATCH('/api/v1/inboxes/{inbox_id}/classification/resources/{kind}/{resource_id}', { params: { path: { inbox_id: inboxId, kind, resource_id: id } }, body, cache: 'no-store' })
    if (!data || !response.ok) throw new ClassificationRequestError(response.status, error)
    return data
  }, onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.classification(inboxId) }) })
}

export function useDeleteMailboxResource(inboxId: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({ gcTime: 0, retry: false, mutationFn: async ({ kind, id, deleteContents }: { kind: MailboxResourceKind; id: string; deleteContents: boolean }) => {
    const client = await getClient()
    const { error, response } = await client.DELETE('/api/v1/inboxes/{inbox_id}/classification/resources/{kind}/{resource_id}', { params: { path: { inbox_id: inboxId, kind, resource_id: id } }, body: { delete_contents: deleteContents }, cache: 'no-store' })
    if (!response.ok) throw new ClassificationRequestError(response.status, error)
  }, onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.classification(inboxId) }) })
}

export function useClassificationStatus(inboxId: string) {
  const getClient = useAuthenticatedClient()
  return useQuery({ queryKey: queryKeys.classificationStatus(inboxId), queryFn: async () => {
    const client = await getClient()
    const { data, error, response } = await client.GET('/api/v1/inboxes/{inbox_id}/classification/status', { params: { path: { inbox_id: inboxId } }, cache: 'no-store' })
    if (!data || !response.ok) throw new ClassificationRequestError(response.status, error)
    return data
  }, enabled: !!inboxId, gcTime: 0, retry: false })
}

export function useClassificationExamples(inboxId: string) {
  const getClient = useAuthenticatedClient()
  return useMutation({ mutationFn: async () => {
    const client = await getClient()
    const query = { limit: 5 } satisfies NonNullable<operations['classification_examples_api_v1_inboxes__inbox_id__classification_examples_get']['parameters']['query']>
    const { data, error, response } = await client.GET('/api/v1/inboxes/{inbox_id}/classification/examples', { params: { path: { inbox_id: inboxId }, query }, cache: 'no-store' })
    if (!data || !response.ok) throw new ClassificationRequestError(response.status, error)
    return data
  }, gcTime: 0, retry: false })
}

export function useClassificationPreview(inboxId: string) {
  const getClient = useAuthenticatedClient()
  return useMutation({ mutationFn: async (body: components['schemas']['ClassificationPreviewRequest']) => {
    const client = await getClient()
    const { data, error, response } = await client.POST('/api/v1/inboxes/{inbox_id}/classification/preview', { params: { path: { inbox_id: inboxId } }, body, cache: 'no-store' })
    if (!data || !response.ok) throw new ClassificationRequestError(response.status, error)
    return data
  }, gcTime: 0, retry: false })
}

export function useActivateClassification(inboxId: string) {
  const getClient = useAuthenticatedClient()
  const queryClient = useQueryClient()
  return useMutation({ mutationFn: async (body: components['schemas']['ClassificationActivateRequest']) => {
    const client = await getClient()
    const { data, error, response } = await client.POST('/api/v1/inboxes/{inbox_id}/classification/activate', { params: { path: { inbox_id: inboxId } }, body, cache: 'no-store' })
    if (!data || !response.ok) throw new ClassificationRequestError(response.status, error)
    return data
  }, gcTime: 0, retry: false, onSettled: () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.classificationStatus(inboxId) })
    queryClient.invalidateQueries({ queryKey: queryKeys.inboxes })
    queryClient.invalidateQueries({ queryKey: queryKeys.onboardingStatus })
  } })
}

// =============================================================================
// Inbox Mutations
// =============================================================================

export function useUpdateInbox() {
  const queryClient = useQueryClient()
  const getClient = useAuthenticatedClient()

  return useMutation({
    mutationFn: async ({ inboxId, data }: { inboxId: string; data: { is_active?: boolean } }) => {
      const client = await getClient()
      const { data: result, error } = await client.PATCH('/api/v1/inboxes/{inbox_id}', {
        params: { path: { inbox_id: inboxId } },
        body: data,
      })
      if (error) throw new Error('Failed to update inbox')
      return result!
    },
    onMutate: async ({ inboxId, data }) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: queryKeys.inboxes })

      // Snapshot the previous value
      const previousInboxes = queryClient.getQueryData<EmailAccount[]>(queryKeys.inboxes)

      // Optimistically update the cache
      if (previousInboxes) {
        queryClient.setQueryData<EmailAccount[]>(
          queryKeys.inboxes,
          previousInboxes.map((inbox) =>
            inbox.uuid === inboxId ? { ...inbox, ...data } : inbox
          )
        )
      }

      return { previousInboxes }
    },
    onError: (_err, _variables, context) => {
      // Rollback on error
      if (context?.previousInboxes) {
        queryClient.setQueryData(queryKeys.inboxes, context.previousInboxes)
      }
    },
    onSettled: () => {
      // Always refetch to ensure consistency with server
      queryClient.invalidateQueries({ queryKey: queryKeys.inboxes })
    },
  })
}

export function useDeleteInbox() {
  const queryClient = useQueryClient()
  const getClient = useAuthenticatedClient()

  return useMutation({
    retry: false,
    mutationFn: async (inboxId: string) => {
      const client = await getClient()
      const { error } = await client.DELETE('/api/v1/inboxes/{inbox_id}', {
        params: { path: { inbox_id: inboxId } },
      })
      if (error) throw new Error('Failed to delete inbox')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.inboxes })
    },
  })
}

// =============================================================================
// Instructions Queries & Mutations
// =============================================================================

export function useInstructions(
  params?: { scope?: InstructionScope; connectedAccountId?: string; mcpServerId?: string; toolName?: string },
  options?: Omit<UseQueryOptions<Instruction[]>, 'queryKey' | 'queryFn'>
) {
  const getClient = useAuthenticatedClient()
  
  return useQuery({
    queryKey: queryKeys.instructions(params),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/api/v1/instructions', {
        cache: 'no-store',
        params: {
          query: {
            scope: params?.scope,
            connected_account_id: params?.connectedAccountId,
            mcp_server_id: params?.mcpServerId,
            tool_name: params?.toolName,
          } satisfies InstructionsQueryParams,
        },
      })
      if (error) throw new Error('Failed to fetch instructions')
      return data ?? []
    },
    ...options,
  })
}

export function useCreateInstruction() {
  const queryClient = useQueryClient()
  const getClient = useAuthenticatedClient()

  return useMutation({
    gcTime: 0,
    mutationFn: async (data: InstructionCreate) => {
      const client = await getClient()
      const { data: result, error } = await client.POST('/api/v1/instructions', {
        body: data,
        cache: 'no-store',
      })
      if (error) throw new Error('Failed to create instruction')
      return result!
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instructions'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.inboxes })
      queryClient.invalidateQueries({ queryKey: queryKeys.onboardingStatus })
    },
  })
}

export function useUpdateInstruction() {
  const queryClient = useQueryClient()
  const getClient = useAuthenticatedClient()

  return useMutation({
    gcTime: 0,
    mutationFn: async ({
      id,
      data,
    }: {
      id: string
      data: InstructionUpdate
    }) => {
      const client = await getClient()
      const { data: result, error, response } = await client.PATCH('/api/v1/instructions/{instruction_id}', {
        params: { path: { instruction_id: id } },
        body: data,
        cache: 'no-store',
      })
      if (error || !result) throw new ClassificationRequestError(response.status, error)
      return result!
    },
    onMutate: async ({ id, data }) => {
      // Cancel all instruction queries to prevent overwrites
      await queryClient.cancelQueries({ queryKey: ['instructions'] })

      // Get all instruction query caches and update them optimistically
      const previousData = new Map<unknown, Instruction[]>()

      queryClient.getQueriesData<Instruction[]>({ queryKey: ['instructions'] }).forEach(
        ([key, data]) => {
          if (data) previousData.set(key, data)
        }
      )

      queryClient.setQueriesData<Instruction[]>(
        { queryKey: ['instructions'] },
        (old) => {
          if (!old) return old
          // Store previous data for rollback
          return old.map((instruction) =>
            instruction.uuid === id ? {
              ...instruction,
              title: data.title ?? instruction.title,
              content: data.content ?? instruction.content,
              order: data.order ?? instruction.order,
              is_active: data.isActive ?? instruction.is_active,
            } : instruction
          )
        }
      )

      return { previousData }
    },
    onError: (_err, _variables, context) => {
      // Rollback all instruction caches on error
      context?.previousData?.forEach((data, key) => {
        queryClient.setQueryData(key as readonly unknown[], data)
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['instructions'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.inboxes })
      queryClient.invalidateQueries({ queryKey: queryKeys.onboardingStatus })
    },
  })
}

export function useDeleteInstruction() {
  const queryClient = useQueryClient()
  const getClient = useAuthenticatedClient()

  return useMutation({
    gcTime: 0,
    mutationFn: async ({ id, version }: { id: string; version: number }) => {
      const client = await getClient()
      const { error, response } = await client.DELETE('/api/v1/instructions/{instruction_id}', {
        params: { path: { instruction_id: id }, query: { version } },
        cache: 'no-store',
      })
      if (error) throw new ClassificationRequestError(response.status, error)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['instructions'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.inboxes })
      queryClient.invalidateQueries({ queryKey: queryKeys.onboardingStatus })
    },
  })
}

export function useReorderInstructions() {
  const queryClient = useQueryClient()
  const getClient = useAuthenticatedClient()

  return useMutation({
    gcTime: 0,
    mutationFn: async (items: components['schemas']['InstructionReorderItem'][]) => {
      const client = await getClient()
      const { error } = await client.POST('/api/v1/instructions/reorder', {
        body: { items },
      })
      if (error) throw new Error('Failed to reorder instructions')
    },
    onMutate: async (items) => {
      await queryClient.cancelQueries({ queryKey: ['instructions'] })

      // Create order lookup map
      const orderMap = new Map(items.map((item) => [item.id, item.order]))

      // Update all instruction query caches
      queryClient.setQueriesData<Instruction[]>(
        { queryKey: ['instructions'] },
        (old) => {
          if (!old) return old
          return [...old]
            .map((instruction) => ({
              ...instruction,
              order: orderMap.get(instruction.uuid) ?? instruction.order,
            }))
            .sort((a, b) => a.order - b.order)
        }
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['instructions'] })
      queryClient.invalidateQueries({ queryKey: queryKeys.inboxes })
      queryClient.invalidateQueries({ queryKey: queryKeys.onboardingStatus })
    },
  })
}

// =============================================================================
// Dashboard Queries
// =============================================================================

export function useDashboardStats(
  options?: Omit<UseQueryOptions<DashboardStats>, 'queryKey' | 'queryFn'>
) {
  const getClient = useAuthenticatedClient()
  
  return useQuery({
    queryKey: queryKeys.dashboardStats,
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/api/v1/dashboard/stats')
      if (error) throw new Error('Failed to fetch dashboard stats')
      return data!
    },
    ...options,
  })
}

// =============================================================================
// Events Queries
// =============================================================================

export function useEvents(
  params?: {
    emailAccountId?: string
    status?: string
    limit?: number
    offset?: number
  },
  options?: Omit<UseQueryOptions<EventsList>, 'queryKey' | 'queryFn'>
) {
  const getClient = useAuthenticatedClient()
  
  return useQuery({
    queryKey: queryKeys.events(params),
    queryFn: async () => {
      const client = await getClient()
      // Use `satisfies` to ensure query params exactly match the API schema.
      // This catches bugs like using "email_account_id" when API expects "connected_account_id".
      const query = {
        connected_account_id: params?.emailAccountId,
        status: params?.status,
        limit: params?.limit,
        offset: params?.offset,
      } satisfies EventsQueryParams
      const { data, error } = await client.GET('/api/v1/events', {
        params: { query },
        cache: 'no-store',
      })
      if (error) throw new Error('Failed to fetch events')
      return data ?? { events: [], total: 0 }
    },
    ...options,
  })
}

// Sender and subject, read live from the mailbox (never stored). Loaded after
// the list renders; null means the email is gone or can't be read right now.
export function useEventHeaders(eventIds: string[]) {
  const getClient = useAuthenticatedClient()
  return useQuery({
    queryKey: ['eventHeaders', eventIds],
    queryFn: async () => {
      const client = await getClient()
      const query = { ids: eventIds } satisfies EventHeadersQueryParams
      const { data, error } = await client.GET('/api/v1/events/headers', {
        params: { query },
        cache: 'no-store',
      })
      if (error) throw new Error('Failed to fetch email headers')
      return data ?? {}
    },
    enabled: eventIds.length > 0,
    staleTime: 5 * 60 * 1000,
    retry: false,
  })
}

export function useEvent(
  eventId: string,
  options?: Omit<UseQueryOptions<EmailEventDetail>, 'queryKey' | 'queryFn'>
) {
  const getClient = useAuthenticatedClient()
  
  return useQuery({
    queryKey: queryKeys.event(eventId),
    queryFn: async () => {
      const client = await getClient()
      const { data, error } = await client.GET('/api/v1/events/{event_id}', {
        params: { path: { event_id: eventId } },
        cache: 'no-store',
      })
      if (error) throw new Error('Failed to fetch event')
      return data!
    },
    enabled: !!eventId,
    ...options,
  })
}

type EventsStats = { total: number; pending: number; analyzing: number; done: number; error: number }

export function useEventsStats(
  params?: { emailAccountId?: string },
  options?: Omit<UseQueryOptions<EventsStats>, 'queryKey' | 'queryFn'>
) {
  const getClient = useAuthenticatedClient()

  return useQuery({
    queryKey: queryKeys.eventsStats(params),
    queryFn: async (): Promise<EventsStats> => {
      const client = await getClient()
      // Use `satisfies` to ensure query params exactly match the API schema.
      const query = {
        connected_account_id: params?.emailAccountId,
      } satisfies EventsStatsQueryParams
      const { data, error } = await client.GET('/api/v1/events/stats/summary', {
        params: { query },
      })
      if (error) throw new Error('Failed to fetch events stats')
      return (data as EventsStats) ?? { total: 0, pending: 0, analyzing: 0, done: 0, error: 0 }
    },
    ...options,
  })
}

// =============================================================================
// Onboarding Hooks
// =============================================================================

export type OnboardingStatus = components['schemas']['OnboardingStatusResponse']

export function useOnboardingStatus(
  options?: Omit<UseQueryOptions<OnboardingStatus>, 'queryKey' | 'queryFn'>
) {
  const getClient = useAuthenticatedClient()

  return useQuery({
    queryKey: queryKeys.onboardingStatus,
    queryFn: async (): Promise<OnboardingStatus> => {
      const client = await getClient()
      const { data, error } = await client.GET('/api/v1/onboarding/status')
      if (error || !data) throw new Error('Failed to fetch onboarding status')
      return data
    },
    ...options,
  })
}

/**
 * Get OAuth authorization URL with optional return_to path.
 * Used by onboarding to redirect back to /onboarding after OAuth completes.
 */
export function useGetOAuthUrl() {
  const getClient = useAuthenticatedClient()

  return useMutation({
    mutationFn: async (params: {
      provider: 'google' | 'microsoft'
      returnTo?: string
    }) => {
      const client = await getClient()
      const endpoint = params.provider === 'google'
        ? '/api/v1/oauth/google/authorize-url'
        : '/api/v1/oauth/microsoft/authorize-url'

      const { data, error } = await client.GET(endpoint, {
        params: {
          query: {
            return_to: params.returnTo,
          },
        },
      })
      if (error) throw new Error('Failed to get OAuth URL')
      return data as { url: string }
    },
  })
}

// =============================================================================
// Subscription Hooks
// =============================================================================

export type SubscriptionResponse = {
  subscription: {
    stripe_customer_id: string
    stripe_subscription_id: string | null
    tier: 'SOLO' | 'PRO' | 'BUSINESS'
    status: 'ACTIVE' | 'PAST_DUE' | 'CANCELED' | 'INCOMPLETE' | 'TRIALING' | 'UNPAID'
    current_period_start: string
    current_period_end: string
    email_limit: number
    inbox_limit: number
    cancel_at_period_end: boolean
    uuid: string
    created_at: string
    updated_at: string
  } | null
  has_subscription: boolean
}

export function useSubscription(
  options?: Omit<UseQueryOptions<SubscriptionResponse>, 'queryKey' | 'queryFn'>
) {
  const getClient = useAuthenticatedClient()

  return useQuery({
    queryKey: queryKeys.subscription,
    queryFn: async (): Promise<SubscriptionResponse> => {
      const client = await getClient()
      const { data, error } = await client.GET('/api/v1/subscription')
      if (error) throw new Error('Failed to fetch subscription')
      return data as SubscriptionResponse
    },
    ...options,
  })
}

export type UsageResponse = {
  stats: {
    emails_used: number
    emails_limit: number
    emails_remaining: number
    inboxes_used: number
    inboxes_limit: number
    inboxes_remaining: number
    period_start: string
    period_end: string
    days_remaining: number
    usage_percentage: number
  }
  projection: {
    current_usage: number
    projected_usage: number
    limit: number
    will_exceed: boolean
    projected_exceed_date: string | null
    daily_average: number
  }
  daily_usage: Array<{
    date: string
    emails_processed: number
    cumulative: number
  }>
}

export function useSubscriptionUsage(
  options?: Omit<UseQueryOptions<UsageResponse>, 'queryKey' | 'queryFn'>
) {
  const getClient = useAuthenticatedClient()

  return useQuery({
    queryKey: queryKeys.subscriptionUsage,
    queryFn: async (): Promise<UsageResponse> => {
      const client = await getClient()
      const { data, error } = await client.GET('/api/v1/subscription/usage')
      if (error) throw new Error('Failed to fetch usage')
      return data as UsageResponse
    },
    ...options,
  })
}

export type TierInfo = {
  tier: 'SOLO' | 'PRO' | 'BUSINESS'
  name: string
  price_cents: number
  email_limit: number
  inbox_limit: number
  features: string[]
}

export function useSubscriptionTiers(
  options?: Omit<UseQueryOptions<TierInfo[]>, 'queryKey' | 'queryFn'>
) {
  const getClient = useAuthenticatedClient()

  return useQuery({
    queryKey: queryKeys.subscriptionTiers,
    queryFn: async (): Promise<TierInfo[]> => {
      const client = await getClient()
      const { data, error } = await client.GET('/api/v1/subscription/tiers')
      if (error) throw new Error('Failed to fetch tiers')
      return data as TierInfo[]
    },
    staleTime: 1000 * 60 * 60, // Tiers don't change often, cache for 1 hour
    ...options,
  })
}

export function useCreateCheckout() {
  const getClient = useAuthenticatedClient()

  return useMutation({
    mutationFn: async (params: {
      tier: 'SOLO' | 'PRO' | 'BUSINESS'
      successUrl: string
      cancelUrl: string
    }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/api/v1/subscription/checkout', {
        body: {
          tier: params.tier,
          success_url: params.successUrl,
          cancel_url: params.cancelUrl,
        },
      })
      if (error) throw new Error('Failed to create checkout session')
      return data as { checkout_url: string }
    },
  })
}

export function useCreatePortal() {
  const getClient = useAuthenticatedClient()

  return useMutation({
    mutationFn: async (params: { returnUrl: string }) => {
      const client = await getClient()
      const { data, error } = await client.POST('/api/v1/subscription/portal', {
        body: {
          return_url: params.returnUrl,
        },
      })
      if (error) throw new Error('Failed to create portal session')
      return data as { portal_url: string }
    },
  })
}

// =============================================================================
// Promo Code Hooks
// =============================================================================

export type PromoCodeValidation = {
  is_valid: boolean
  tier: 'SOLO' | 'PRO' | 'BUSINESS' | null
  tier_name: string | null
  email_limit: number | null
  inbox_limit: number | null
  error: string | null
}

export type PromoCodeRedeemResponse = {
  success: boolean
  subscription: SubscriptionResponse['subscription']
  message: string
}

/**
 * Validate a promo code without authentication.
 * Used to check if a code is valid before showing the invite UI.
 */
export function useValidatePromoCode(
  code: string | undefined,
  options?: Omit<UseQueryOptions<PromoCodeValidation>, 'queryKey' | 'queryFn'>
) {
  const getClient = useAuthenticatedClient()

  return useQuery({
    queryKey: ['promo-code', 'validate', code] as const,
    queryFn: async (): Promise<PromoCodeValidation> => {
      if (!code) {
        return {
          is_valid: false,
          tier: null,
          tier_name: null,
          email_limit: null,
          inbox_limit: null,
          error: null,
        }
      }
      const client = await getClient()
      const { data, error } = await client.GET('/api/v1/subscription/validate-promo', {
        params: { query: { code } },
      })
      if (error) throw new Error('Failed to validate promo code')
      return data as PromoCodeValidation
    },
    enabled: !!code,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    ...options,
  })
}

/**
 * Redeem a promo code to get a free subscription.
 */
export function useRedeemPromoCode() {
  const queryClient = useQueryClient()
  const getClient = useAuthenticatedClient()

  return useMutation({
    mutationFn: async (code: string): Promise<PromoCodeRedeemResponse> => {
      const client = await getClient()
      const { data, error } = await client.POST('/api/v1/subscription/redeem-promo', {
        body: { code },
      })
      if (error) {
        // Extract error message from response
        const errorData = error as { detail?: string }
        throw new Error(errorData.detail || 'Failed to redeem promo code')
      }
      return data as PromoCodeRedeemResponse
    },
    onSuccess: () => {
      // Invalidate subscription queries to reflect new subscription
      queryClient.invalidateQueries({ queryKey: queryKeys.subscription })
      queryClient.invalidateQueries({ queryKey: queryKeys.onboardingStatus })
      queryClient.invalidateQueries({ queryKey: queryKeys.inboxes })
    },
  })
}
